import * as React from 'react'

import { HorizontalAlignment, i18n } from '@thg-commerce/enterprise-core'
import { ProductBlockProps } from '@thg-commerce/enterprise-components'
import { ButtonEmphases } from '@thg-commerce/gravity-elements'

import { TextColor } from './types'

import {
  ProductBlockItem,
  Title,
  Subtitle,
  ProductBlockContainer,
  CTAWrapper,
  StyledButton,
} from './styles'

export interface ProductProps extends Omit<ProductBlockProps, 'review'> {
  review?: {
    starRating: number
    numberOfReviews: number
  }
  attributes: {
    justifyContent: 'start' | 'center'
    alignItems?: 'start' | 'center'
  }
}

export interface FeaturedProductsProps {
  title?: {
    text: string
    color?: TextColor
    marginBottom?: number
    horizontalAlignment?: HorizontalAlignment
  }
  subtitle?: {
    text: string
    color?: TextColor
    marginBottom?: number
    horizontalAlignment?: HorizontalAlignment
  }
  productBlocks: {
    horizontalAlignment?: HorizontalAlignment
    items: ProductProps[]
  }
  cta?: {
    text: string
    emphasis?: ButtonEmphases
    marginTop?: number
    horizontalAlignment?: HorizontalAlignment
  }
}

export const FeaturedProducts = (props: FeaturedProductsProps) => {
  if (props.productBlocks.items.length < 1) {
    return null
  }

  const i18nText = {
    twoBestSellers: i18n('twobestsellers.title.text'),
    reviewsStarsLabel: i18n('reviews.stars.label'),
    reviewsLabel: i18n('reviews.reviews.label'),
  }

  const screenReaderReviewText = ({ score, total }) =>
    `${score} ${i18nText.reviewsStarsLabel}
${total} ${i18nText.reviewsLabel}`

  const products = props.productBlocks.items.map((product, index) => {
    return (
      <ProductBlockItem
        {...product}
        review={
          product.review
            ? {
                ...product.review,
                screenReaderOnlyText: screenReaderReviewText({
                  score: product.review?.starRating,
                  total: product.review?.numberOfReviews,
                }),
              }
            : undefined
        }
        key={index}
        totalItems={props.productBlocks.items.length}
      />
    )
  })

  return (
    <React.Fragment>
      {props.title && (
        <Title
          marginBottom={props.title.marginBottom}
          horizontalAlignment={props.title.horizontalAlignment || 'center'}
        >
          {props.title.text || i18nText.twoBestSellers}
        </Title>
      )}
      {props.subtitle && (
        <Subtitle
          marginBottom={props.subtitle.marginBottom}
          horizontalAlignment={props.subtitle.horizontalAlignment || 'center'}
        >
          {props.subtitle.text}
        </Subtitle>
      )}
      <ProductBlockContainer
        horizontalAlignment={
          props.productBlocks.horizontalAlignment || 'center'
        }
      >
        {products}
      </ProductBlockContainer>
      {props.cta && (
        <CTAWrapper
          horizontalAlignment={props.cta.horizontalAlignment || 'center'}
          marginTop={props.cta.marginTop}
        >
          <StyledButton emphasis={props.cta.emphasis || 'medium'}>
            {props.cta.text}
          </StyledButton>
        </CTAWrapper>
      )}
    </React.Fragment>
  )
}
